/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { FileText, Upload, Trash2, AlertCircle, CheckCircle, Clock, X, Calendar, Eye } from "lucide-react";
import { 
  uploadVendorDocument, 
  getVendorDocuments, 
  deleteVendorDocument,
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_LABELS 
} from "../../../services/VendorDocumentService";

const DocumentsContent = ({ user }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  
  const [uploadForm, setUploadForm] = useState({
    file: null,
    name: '',
    type: DOCUMENT_TYPES.OTHER,
    description: '',
    expiresAt: ''
  });

  useEffect(() => {
    if (user?.uid) {
      loadDocuments();
    }
  }, [user]);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const result = await getVendorDocuments(user.uid);
      if (result.success) {
        setDocuments(result.documents);
      }
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm(prev => ({
        ...prev,
        file,
        name: prev.name || file.name.replace(/\.[^/.]+$/, "")
      }));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file) return;
    
    setUploading(true);
    setError(null);
    
    try {
      const result = await uploadVendorDocument(user.uid, uploadForm.file, {
        name: uploadForm.name,
        type: uploadForm.type,
        description: uploadForm.description,
        expiresAt: uploadForm.expiresAt ? new Date(uploadForm.expiresAt) : null
      });
      
      if (result.success) {
        setIsModalOpen(false);
        setUploadForm({
          file: null,
          name: '',
          type: DOCUMENT_TYPES.OTHER,
          description: '',
          expiresAt: ''
        });
        await loadDocuments();
      } else {
        setError(result.error || 'Failed to upload document');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      const result = await deleteVendorDocument(user.uid, documentId);
      if (result.success) {
        await loadDocuments();
      }
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'valid':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3" /> Valid
          </span>
        );
      case 'expiring_soon':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3" /> Expiring Soon
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <AlertCircle className="w-3 h-3" /> Expired
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No expiration';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 transition-opacity duration-300">
        <p className="text-sm text-grey-500">Loading documents...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-grey-900">Business Documents</h2>
        <p className="text-sm text-grey-600 mt-1">
          Upload your business documents such as certificates of insurance, permits, and licenses. 
          These documents will be shared with event organizers when you apply to events.
        </p>
      </div>

      {documents.some(d => d.status === 'expired') && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Some documents have expired</p>
            <p className="text-xs text-red-600">Please update your expired documents to maintain compliance.</p>
          </div>
        </div>
      )}

      {documents.some(d => d.status === 'expiring_soon') && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
          <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800">Documents expiring soon</p>
            <p className="text-xs text-yellow-600">Some documents will expire within 30 days.</p>
          </div>
        </div>
      )}

      {documents.length > 0 ? (
        <div className="space-y-3 mb-6">
          {documents.map((doc) => (
            <div 
              key={doc.id} 
              className={`bg-white border rounded-xl p-4 flex items-center justify-between ${
                doc.status === 'expired' ? 'border-red-200 bg-red-50' : 'border-grey-200'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  doc.status === 'expired' ? 'bg-red-100' : 'bg-grey-100'
                }`}>
                  <FileText className={`w-5 h-5 ${doc.status === 'expired' ? 'text-red-600' : 'text-grey-600'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-grey-900">{doc.name}</h4>
                    {getStatusBadge(doc.status)}
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-grey-500">{DOCUMENT_TYPE_LABELS[doc.type] || doc.type}</span>
                    {doc.expiresAt && (
                      <span className="text-xs text-grey-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Expires: {formatDate(doc.expiresAt)}
                      </span>
                    )}
                    <span className="text-xs text-grey-400">{formatFileSize(doc.fileSize)}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={doc.downloadURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-grey-500 hover:text-grey-700 hover:bg-grey-100 rounded-lg transition"
                  title="View Document"
                >
                  <Eye className="w-4 h-4" />
                </a>
                <button
                  onClick={() => handleDelete(doc.id)}
                  className="p-2 text-grey-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                  title="Delete Document"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-grey-50 border border-dashed border-grey-300 rounded-xl p-8 text-center mb-6">
          <FileText className="w-12 h-12 text-grey-400 mx-auto mb-3" />
          <h3 className="font-medium text-grey-700 mb-1">No documents uploaded</h3>
          <p className="text-sm text-grey-500">Upload your business documents to share them with event organizers.</p>
        </div>
      )}

      <button
        onClick={() => setIsModalOpen(true)}
        className="flex items-center gap-2 px-4 py-2 bg-pebble-red text-white rounded-lg hover:bg-red-700 transition font-medium"
      >
        <Upload className="w-4 h-4" />
        Upload Document
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-grey-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-grey-900">Upload Document</h3>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-1 hover:bg-grey-100 rounded-lg transition"
              >
                <X className="w-5 h-5 text-grey-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">File</label>
                <div className="border-2 border-dashed border-grey-300 rounded-lg p-4 text-center hover:border-grey-400 transition cursor-pointer">
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="doc-upload"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  <label htmlFor="doc-upload" className="cursor-pointer">
                    {uploadForm.file ? (
                      <div className="flex items-center justify-center gap-2 text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">{uploadForm.file.name}</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-grey-400 mx-auto mb-2" />
                        <p className="text-sm text-grey-600">Click to select a file</p>
                        <p className="text-xs text-grey-400 mt-1">PDF, DOC, JPG, PNG (max 10MB)</p>
                      </>
                    )}
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">Document Name</label>
                <input
                  type="text"
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-pebble-red focus:border-transparent"
                  placeholder="e.g., Certificate of Insurance 2025"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">Document Type</label>
                <select
                  value={uploadForm.type}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-pebble-red focus:border-transparent"
                >
                  {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">Expiration Date (Optional)</label>
                <input
                  type="date"
                  value={uploadForm.expiresAt}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, expiresAt: e.target.value }))}
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-pebble-red focus:border-transparent"
                  min={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-grey-500 mt-1">We'll notify you before this document expires.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">Description (Optional)</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-pebble-red focus:border-transparent"
                  rows={2}
                  placeholder="Add any notes about this document..."
                />
              </div>
            </div>

            <div className="p-6 border-t border-grey-200 flex gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!uploadForm.file || !uploadForm.name || uploading}
                className="flex-1 px-4 py-2 bg-pebble-red text-white rounded-lg hover:bg-red-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsContent;
